"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePathname } from "next/navigation";
import Link from "next/link";
import AreaContentBlock from "../../components/AreaContentBlock";
import { getLocaleFromPath } from "../../lib/i18n/utils";

const OVERLAY_COLOR = "#1E2E25";
const OVERLAY_OPACITY = 0.85;
const TRANSITION_DURATION = 1000;

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export default function AreaPageClient() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const [hoveredSide, setHoveredSide] = useState<"left" | "right" | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkDesktop = () => {
      if (typeof window === "undefined") return;
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setScrollProgress(0);
      return;
    }

    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerTop = container.offsetTop;
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      const scrollOffset = Math.max(0, scrollY - containerTop);
      const rawProgress = Math.max(
        0,
        Math.min(1, scrollOffset / viewportHeight)
      );
      
      setScrollProgress(easeInOutCubic(rawProgress));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDesktop]);

  const opacities = useMemo(() => {
    if (isDesktop) {
      const isRightHovered = hoveredSide === "right";
      return {
        left: isRightHovered ? 0 : 1,
        right: isRightHovered ? 1 : 0,
        leftOverlay: isRightHovered ? 0 : OVERLAY_OPACITY,
        rightOverlay: isRightHovered ? OVERLAY_OPACITY : 0,
      };
    }
    
    return {
      left: 1 - scrollProgress,
      right: scrollProgress,
      leftOverlay: (1 - scrollProgress) * OVERLAY_OPACITY,
      rightOverlay: scrollProgress * OVERLAY_OPACITY,
    };
  }, [isDesktop, hoveredSide, scrollProgress]);

  const handleMouseEnter = useCallback((side: "left" | "right") => {
    setHoveredSide(side);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredSide(null);
  }, []);

  const sideClasses = "relative h-screen min-h-[25rem] md:min-h-[31.25rem] lg:min-h-0 lg:sticky lg:top-0";
  const contentClasses = "relative h-full flex items-center justify-center px-4 py-10 sm:px-5 sm:py-12 md:px-8 md:py-16 lg:px-12 lg:py-20 xl:px-32 transition-opacity ease-out";
  const overlayClasses = "absolute inset-0 transition-opacity ease-out";

  return (
    <main 
      className="relative w-full overflow-x-hidden"
      onMouseLeave={handleMouseLeave}
    >
      <div 
        ref={containerRef}
        className="grid grid-cols-1 lg:grid-cols-2 h-[200vh] lg:h-screen"
      >
        <div
          className={sideClasses}
          onMouseEnter={() => {
            if (isDesktop) handleMouseEnter("left");
          }}
          style={{
            backgroundImage: "url(/images/area/area-left.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            className={overlayClasses}
            style={{
              backgroundColor: OVERLAY_COLOR,
              opacity: opacities.leftOverlay,
              transitionDuration: `${TRANSITION_DURATION}ms`,
            }}
          />
          <div
            className={contentClasses}
            style={{
              opacity: opacities.left,
              transitionDuration: `${TRANSITION_DURATION}ms`,
            }}
          >
            <AreaContentBlock
              title={
                <Link href={locale === "el" ? "/area" : `/${locale}/area`} className="hover:underline transition-colors">
                  {t("Kastoria")}
                </Link>
              }
              text={
                <>
                  {t("Timeless, always attractive, with a changing face yet ever new, it still continues its path carved by Aeolians and Macedonians, Romans and Christians. Built stone by stone from its towering castles to its Byzantine churches and mansions that stand patiently against the tide of time. In very few places did Byzantium seal its essence so deeply as in this city. Kastoria doesn't just have history; it is history itself, fully alive, standing before you. It overwhelms you, challenges you to gather it piece by piece from what was scattered by the hands of conquerors, barbarians, and the passage of time.")}
                  {" "}
                  <Link href={locale === "el" ? "/rooms" : `/${locale}/rooms`} className="underline hover:text-white transition-colors decoration-white/50 hover:decoration-white">
                    {t("Discover our rooms")}
                  </Link> {t("near this historic city.")}
                </>
              }
            />
          </div>
        </div>

        <div
          className={sideClasses}
          onMouseEnter={() => {
            if (isDesktop) handleMouseEnter("right");
          }}
          style={{
            backgroundImage: "url(/images/area/area-right.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            className={overlayClasses}
            style={{
              backgroundColor: OVERLAY_COLOR,
              opacity: opacities.rightOverlay,
              transitionDuration: `${TRANSITION_DURATION}ms`,
            }}
          />
          <div
            className={contentClasses}
            style={{
              opacity: opacities.right,
              transitionDuration: `${TRANSITION_DURATION}ms`,
            }}
          >
            <AreaContentBlock
              title={
                <Link href={locale === "el" ? "/area" : `/${locale}/area`} className="hover:underline transition-colors">
                  {t("Grammos")}
                </Link>
              }
              text={
                <>
                  {t("The mountainous mass of Grammos is a place of incomparable beauty and perhaps one of the few unexplored destinations in Greece. Here you will encounter extensive meadows, alpine rivers, virgin forests. The hikes on the labyrinthine paths of the mountain are exciting, full of surprises, and offer unique emotions to the hiker.")}
                  {" "}
                  <Link href={locale === "el" ? "/where" : `/${locale}/where`} className="underline hover:text-white transition-colors decoration-white/50 hover:decoration-white">
                    {t("Find us")}
                  </Link> {t("in this beautiful region.")}
                </>
              }
            />
          </div>
        </div>
      </div>
    </main>
  );
}

